import React from "react";
import { Menu } from "lucide-react";
import { Link } from "react-router-dom";

export default function Header({ onMenuClick, menuOpen = false }) {
  return (
    <header
      data-testid="qd-header"
      className="fixed top-0 left-0 right-0 z-[100] pointer-events-none"
    >
      {/* Top scrim — improves legibility on any background */}
      <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black/55 via-black/20 to-transparent" />

      <div className="relative px-4 sm:px-8 lg:px-12 pt-4 sm:pt-5 pb-2 sm:pb-4 pointer-events-auto">
        {/* CENTERED WORDMARK — pill on mobile, masthead on desktop */}
        <div className="absolute inset-x-0 top-0 flex justify-center pt-5 sm:pt-7 pointer-events-none">
          {/* Mobile pill (visible only on small screens) */}
          <span
            data-testid="qd-wordmark"
            className="sm:hidden px-4 py-1 rounded-full bg-white/95 backdrop-blur-md border border-white/70 shadow-[0_4px_18px_rgba(0,0,0,0.25)] text-[#1A1A1A] flex items-baseline gap-1.5 pointer-events-auto"
          >
            <span className="font-serif-display text-base font-medium tracking-tight leading-none">Queens</span>
            <span className="font-serif-display italic text-base font-light leading-none">Daily</span>
          </span>

          {/* Desktop masthead — thin rules + plain serif wordmark */}
          <span
            className="hidden sm:inline-flex items-center gap-5 lg:gap-7 text-white pointer-events-auto"
            style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
          >
            <span className="h-px w-12 lg:w-20 bg-white/65" aria-hidden="true" />
            <span className="inline-flex items-baseline gap-2">
              <span className="font-serif-display text-2xl lg:text-3xl font-medium tracking-tight leading-none">
                Queens
              </span>
              <span className="font-serif-display italic text-2xl lg:text-3xl font-light leading-none">
                Daily
              </span>
            </span>
            <span className="h-px w-12 lg:w-20 bg-white/65" aria-hidden="true" />
          </span>
        </div>

        {/* LEFT: bare circular QD monogram */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="qd-logo"
            aria-label="Queens Daily home"
            className="inline-block rounded-full shadow-[0_4px_18px_rgba(0,0,0,0.30)] hover:shadow-[0_6px_22px_rgba(0,0,0,0.45)] transition-shadow"
          >
            <QDMonogram size={44} sizeLg={60} />
          </Link>

          {/* RIGHT: menu */}
          {!menuOpen && (
            <>
              {/* Mobile: dark circle */}
              <button
                type="button"
                data-testid="menu-toggle-button"
                onClick={onMenuClick}
                aria-label="Open menu"
                className="sm:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#1A1A1A] text-white shadow-[0_4px_18px_rgba(0,0,0,0.35)] border border-white/70 hover:bg-white hover:text-[#1A1A1A] transition-colors"
              >
                <Menu size={18} strokeWidth={1.75} />
              </button>

              {/* Desktop: ghost button — text + thin icon, hairline underline on hover */}
              <button
                type="button"
                data-testid="menu-toggle-button-desktop"
                onClick={onMenuClick}
                aria-label="Open menu"
                className="hidden sm:inline-flex items-center gap-3 lg:gap-4 text-white group transition-colors"
                style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
              >
                <span className="relative overline tracking-[0.32em] text-[11px] lg:text-xs">
                  Menu
                  <span className="absolute left-0 right-0 -bottom-1 h-px bg-white/80 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                </span>
                <span className="flex flex-col gap-[5px] w-7 lg:w-9 group-hover:[&>span]:bg-[#D4A373] transition-colors">
                  <span className="block h-px w-full bg-white" />
                  <span className="block h-px w-full bg-white" />
                  <span className="block h-px w-2/3 ml-auto bg-white" />
                </span>
              </button>
            </>
          )}
          {menuOpen && <span className="w-11 sm:w-[120px] h-11" aria-hidden="true" />}
        </div>
      </div>
    </header>
  );
}

export function QDMonogram({ size = 44, sizeLg, variant = "dark" }) {
  const bg = variant === "dark" ? "#1A1A1A" : "#FDFBF7";
  const fg = variant === "dark" ? "#FDFBF7" : "#1A1A1A";
  const accent = "#D4A373";
  const style = sizeLg
    ? { "--qd-size": `${size}px`, "--qd-size-lg": `${sizeLg}px` }
    : { "--qd-size": `${size}px`, "--qd-size-lg": `${size}px` };
  return (
    <span
      aria-hidden="true"
      className="qd-monogram relative inline-flex items-center justify-center rounded-full shrink-0 border border-white/40"
      style={{ ...style, background: bg, color: fg }}
    >
      <span className="qd-monogram-q font-serif-display font-medium leading-none tracking-tight">Q</span>
      <span className="qd-monogram-d font-serif-display italic font-light leading-none -ml-1" style={{ color: accent }}>D</span>
      <span className="qd-monogram-dot absolute -right-0.5 -bottom-0.5 rounded-full" style={{ background: accent }} />
    </span>
  );
}
